import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline/index.js";
import { BasicTable } from "../../UI/Table/BasicTable.tsx";
import { DropDown } from "../../UI/DropDown/DropDown.tsx";
import { DropDownItem } from "../../UI/DropDown/DropDownItem.tsx";
import { useCopyToClipboard } from "../../UI/Feedback/useCopyToClipboard.ts";

export type OnboardingLivePdfRow = {
  userGroupId: string;
  pdfUrl: string;
  userGroup?: { id: string; slug: string; name: string };
};

type Props = {
  items: OnboardingLivePdfRow[];
  onEdit: (row: OnboardingLivePdfRow) => void;
  onDelete: (row: OnboardingLivePdfRow) => void;
};

export function OnboardingLivePdfTable({ items, onEdit, onDelete }: Props) {
  const copyToClipboard = useCopyToClipboard();
  const columnHelper = createColumnHelper<OnboardingLivePdfRow>();
  const columns = [
    columnHelper.display({
      id: "userGroupSlug",
      header: "User group",
      cell: (v) => v.row.original.userGroup?.slug ?? v.row.original.userGroupId,
    }),
    columnHelper.accessor("pdfUrl", {
      header: "Pdf Url",
      meta: { className: "hidden md:table-cell" },
      cell: (info) => (
        <div
          onClick={() => {
            copyToClipboard(info.getValue());
          }}
          className={
            "max-w-[240px] overflow-hidden overflow-ellipsis cursor-pointer"
          }
        >
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      cell: (v) => (
        <div className={"flex justify-end"}>
          <DropDown
            activator={
              <EllipsisHorizontalIcon
                className={"text-slate-800 w-5 h-5 group-hover:text-white"}
              />
            }
          >
            <DropDownItem onClick={() => onEdit(v.row.original)}>
              <PencilSquareIcon className={"w-5 h-5 mr-2"} />
              Edit
            </DropDownItem>
            <DropDownItem onClick={() => onDelete(v.row.original)}>
              <TrashIcon className={"w-5 h-5 mr-2 text-red-500"} />
              <span className={"text-red-500"}>Delete</span>
            </DropDownItem>
          </DropDown>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    columns,
    data: items,
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
  });

  return <BasicTable table={table} />;
}
